export interface LudoThrowPhysics {
  durationMs: number;
  force: number;
  startX: number;
  startY: number;
  momentumX: number;
  momentumY: number;
  lateMomentumX: number;
  lateMomentumY: number;
  reboundX: number;
  reboundY: number;
  pathTilt: number;
  safeMinX: number;
  safeMaxX: number;
  safeMinY: number;
  safeMaxY: number;
  bounceCount: number;
  wall1X: number;
  wall1Y: number;
  wall2X: number;
  wall2Y: number;
  wall3X: number;
  wall3Y: number;
}

export interface LudoThrowSample {
  distanceX: number;
  distanceY: number;
  maxDrag: number;
  velocityX: number;
  velocityY: number;
}

export interface LudoDragBounds {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

export const ludoDiceRollDurationMs = 1_650;

export const defaultLudoThrowPhysics: LudoThrowPhysics = {
  durationMs: ludoDiceRollDurationMs,
  force: 0.55,
  startX: 0,
  startY: 0,
  momentumX: 0,
  momentumY: 0,
  lateMomentumX: 0,
  lateMomentumY: 0,
  reboundX: 0,
  reboundY: 0,
  pathTilt: 0,
  safeMinX: -180,
  safeMaxX: 180,
  safeMinY: -180,
  safeMaxY: 180,
  bounceCount: 0,
  wall1X: 0,
  wall1Y: 0,
  wall2X: 0,
  wall2Y: 0,
  wall3X: 0,
  wall3Y: 0,
};

export function clampLudoPhysics(value: number, minimum: number, maximum: number) {
  return Math.min(maximum, Math.max(minimum, value));
}

export function hasLudoDiceTouchedEdge(
  distanceX: number,
  distanceY: number,
  bounds: LudoDragBounds,
  tolerance = 0.5,
) {
  return (
    distanceX <= bounds.minX + tolerance ||
    distanceX >= bounds.maxX - tolerance ||
    distanceY <= bounds.minY + tolerance ||
    distanceY >= bounds.maxY - tolerance
  );
}

export function calculateLudoThrowPhysics(sample: LudoThrowSample): LudoThrowPhysics {
  const maxDrag = Math.max(1, sample.maxDrag);
  const startX = clampLudoPhysics(sample.distanceX, -maxDrag, maxDrag);
  const startY = clampLudoPhysics(sample.distanceY, -maxDrag, maxDrag);
  const distance = Math.hypot(startX, startY);
  const speed = Math.hypot(sample.velocityX, sample.velocityY);
  const force = clampLudoPhysics(speed / 1.35 + (distance / maxDrag) * 0.38, 0.18, 1);
  const momentumX = clampLudoPhysics(sample.velocityX * 95 + startX * 0.13, -125, 125);
  const momentumY = clampLudoPhysics(sample.velocityY * 95 + startY * 0.13, -125, 125);

  return {
    durationMs: Math.round(1_950 - force * 700),
    force,
    startX,
    startY,
    momentumX,
    momentumY,
    lateMomentumX: momentumX * 0.24,
    lateMomentumY: momentumY * 0.24,
    reboundX: momentumX * -0.2,
    reboundY: momentumY * -0.2,
    pathTilt: clampLudoPhysics(sample.velocityX * 8, -20, 20),
    safeMinX: defaultLudoThrowPhysics.safeMinX,
    safeMaxX: defaultLudoThrowPhysics.safeMaxX,
    safeMinY: defaultLudoThrowPhysics.safeMinY,
    safeMaxY: defaultLudoThrowPhysics.safeMaxY,
    bounceCount: 0,
    wall1X: 0,
    wall1Y: 0,
    wall2X: 0,
    wall2Y: 0,
    wall3X: 0,
    wall3Y: 0,
  };
}

export function constrainLudoThrowToBoard(
  physics: LudoThrowPhysics,
  boardWidth: number,
  boardHeight: number,
  dieWidth: number,
  dieHeight: number,
  homeOriginX = 0,
  homeOriginY = 0,
): LudoThrowPhysics {
  const inset = Math.max(4, Math.min(boardWidth, boardHeight) * 0.012);
  // A tumbling cube can project beyond its flat width by roughly sqrt(2) / 2.
  // Reserve that full rotating envelope so even a corner cannot cross the board edge.
  const safeX = Math.max(0, boardWidth / 2 - dieWidth * 0.72 - inset);
  const safeY = Math.max(0, boardHeight / 2 - dieHeight * 0.72 - inset);
  const startX = clampLudoPhysics(homeOriginX + physics.startX, -safeX, safeX);
  const startY = clampLudoPhysics(homeOriginY + physics.startY, -safeY, safeY);
  const bounceCount =
    physics.force >= 0.93 ? 3 : physics.force >= 0.82 ? 2 : physics.force >= 0.68 ? 1 : 0;
  const walls = calculateLudoWallContacts(
    startX,
    startY,
    physics.momentumX,
    physics.momentumY,
    safeX,
    safeY,
    bounceCount,
  );

  return {
    ...physics,
    startX: clampLudoPhysics(physics.startX, -safeX, safeX),
    startY: clampLudoPhysics(physics.startY, -safeY, safeY),
    safeMinX: -safeX,
    safeMaxX: safeX,
    safeMinY: -safeY,
    safeMaxY: safeY,
    bounceCount,
    wall1X: walls[0]?.x ?? 0,
    wall1Y: walls[0]?.y ?? 0,
    wall2X: walls[1]?.x ?? 0,
    wall2Y: walls[1]?.y ?? 0,
    wall3X: walls[2]?.x ?? 0,
    wall3Y: walls[2]?.y ?? 0,
  };
}

function calculateLudoWallContacts(
  startX: number,
  startY: number,
  initialVelocityX: number,
  initialVelocityY: number,
  safeX: number,
  safeY: number,
  count: number,
) {
  const contacts: Array<{ x: number; y: number }> = [];
  let x = startX;
  let y = startY;
  let velocityX = initialVelocityX;
  let velocityY = initialVelocityY;
  if (Math.hypot(velocityX, velocityY) < 1) {
    velocityX = Math.abs(startX) > 1 ? -startX : safeX;
    velocityY = Math.abs(startY) > 1 ? -startY : safeY * 0.73;
  }

  for (let bounce = 0; bounce < count; bounce += 1) {
    const atLeft = x <= -safeX + 0.5;
    const atRight = x >= safeX - 0.5;
    const atTop = y <= -safeY + 0.5;
    const atBottom = y >= safeY - 0.5;
    const outwardX = (atLeft && velocityX < 0) || (atRight && velocityX > 0);
    const outwardY = (atTop && velocityY < 0) || (atBottom && velocityY > 0);
    if (outwardX || outwardY) {
      contacts.push({ x, y });
      if (outwardX) velocityX *= -1;
      if (outwardY) velocityY *= -1;
      continue;
    }

    const timeX =
      velocityX > 0
        ? (safeX - x) / velocityX
        : velocityX < 0
          ? (-safeX - x) / velocityX
          : Number.POSITIVE_INFINITY;
    const timeY =
      velocityY > 0
        ? (safeY - y) / velocityY
        : velocityY < 0
          ? (-safeY - y) / velocityY
          : Number.POSITIVE_INFINITY;
    const time = Math.min(
      timeX > 0.0001 ? timeX : Number.POSITIVE_INFINITY,
      timeY > 0.0001 ? timeY : Number.POSITIVE_INFINITY,
    );
    if (!Number.isFinite(time)) break;
    x = clampLudoPhysics(x + velocityX * time, -safeX, safeX);
    y = clampLudoPhysics(y + velocityY * time, -safeY, safeY);
    const hitsX = Math.abs(timeX - time) < 0.0001;
    const hitsY = Math.abs(timeY - time) < 0.0001;
    contacts.push({ x, y });
    if (hitsX) velocityX *= -1;
    if (hitsY) velocityY *= -1;
  }

  return contacts;
}
